import { useState } from "react";
import { Download } from "lucide-react";
import { Banner, Button } from "../../components";
import { recoveryCodesFile } from "../../api/mfa";
import { t } from "../../i18n/t";

/**
 * xmg-sec-mfa: the recovery codes, shown ONCE. The server stores only a hash of each, so this panel is the only
 * place the plaintext ever appears: it offers them as a download and asks the user to confirm they have kept
 * them before moving on. Nothing here persists them - leaving the screen discards them.
 */
export function RecoveryCodesPanel({
  codes,
  account,
  continueLabel,
  onContinue,
}: {
  codes: readonly string[];
  account: string;
  continueLabel: string;
  onContinue: () => void;
}): JSX.Element {
  const [saved, setSaved] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  function onDownload(): void {
    const text = recoveryCodesFile(codes, account, new Date());
    // jsdom has no object URLs; a browser always does. The codes stay visible on screen either way.
    if (typeof URL.createObjectURL !== "function") {
      setDownloaded(true);
      return;
    }
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "lucimigration-recovery-codes.txt";
    anchor.click();
    URL.revokeObjectURL(url);
    setDownloaded(true);
  }

  return (
    <div className="mfacodes">
      <Banner tone="warn">{t("mfa.codes.once")}</Banner>
      <ol className="mfacodes__list mono" aria-label={t("mfa.codes.list_label")}>
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ol>
      <Button onClick={onDownload}>
        <Download size={16} strokeWidth={2} aria-hidden="true" />
        {t("mfa.codes.download")}
      </Button>
      {downloaded ? <p role="status">{t("mfa.codes.downloaded")}</p> : null}
      <label className="mfacodes__ack">
        <input type="checkbox" checked={saved} onChange={(event) => setSaved(event.target.checked)} />
        {t("mfa.codes.saved")}
      </label>
      <Button variant="primary" className="button--auth" disabled={!saved} onClick={onContinue}>
        {continueLabel}
      </Button>
    </div>
  );
}
